import { useEffect, useMemo, useState } from 'react';
import ChartCard from '../components/ChartCard';
import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import ExportButtons from '../components/ExportButtons';
import LoadingState from '../components/LoadingState';
import { useActiveUpload } from '../contexts/ActiveUploadContext';
import { useDateRange } from '../contexts/DateRangeContext';
import { getTeamMemberPerformance } from '../services/reportService';
import { buildTeamContributionSummary, groupTeamPerformanceRows } from '../utils/analytics';
import { MILESTONE_COLUMNS, ROLE_DEFINITIONS } from '../utils/constants';
import { exportRowsToCsv, exportSheetsToXlsx } from '../utils/exporters';

const milestoneSeries = MILESTONE_COLUMNS.map((column) => ({ key: column.key, name: column.label }));
const milestoneColumns = [
  { key: 'member_name', label: 'Team Member' },
  ...MILESTONE_COLUMNS.map((column) => ({ key: column.key, label: column.label, align: 'right' })),
  { key: 'total', label: 'Total', align: 'right' },
];

export default function TeamMemberPerformancePage() {
  const { activeUpload, loading: uploadLoading } = useActiveUpload();
  const { startDate, endDate } = useDateRange();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeUpload?.id) return;
      setLoading(true);
      setError('');
      try {
        const data = await getTeamMemberPerformance(activeUpload.id, startDate, endDate);
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeUpload?.id, startDate, endDate]);

  const grouped = useMemo(() => groupTeamPerformanceRows(rows), [rows]);
  const contributionSummary = useMemo(() => buildTeamContributionSummary(grouped), [grouped]);
  const flatExportRows = useMemo(() => grouped.flatMap((role) => role.rows.map((row) => ({
    Role: role.label,
    'Team Member': row.member_name,
    'Initial Project Delivery': row.initial_project_delivery,
    'Restamp Required': row.restamp_required,
    'Post Install Letter': row.post_install_letter,
    Other: row.other,
    Total: row.total,
  }))), [grouped]);

  const workbookSheets = useMemo(() => {
    const sheets = {
      'Contribution Summary': contributionSummary.map((row) => ({
        'Team Member': row.member_name,
        ...Object.fromEntries(ROLE_DEFINITIONS.map((role) => [role.label, row[role.key] || 0])),
        'Total Contribution': row.total_contribution,
      })),
    };
    grouped.forEach((role) => {
      sheets[role.label] = role.rows.map((row) => ({
        'Team Member': row.member_name,
        'Initial Project Delivery': row.initial_project_delivery,
        'Restamp Required': row.restamp_required,
        'Post Install Letter': row.post_install_letter,
        Other: row.other,
        Total: row.total,
      }));
    });
    return sheets;
  }, [contributionSummary, grouped]);

  if (uploadLoading) return <LoadingState message="Loading active upload..." />;
  if (!activeUpload) return <EmptyState title="No active report" message="Ask an admin to upload and activate a Project Milestone Report." />;

  return (
    <div className="page-stack team-performance-page">
      <div className="page-title-row">
        <div>
          <h2>Team Member Performance Dashboard</h2>
          <p>Milestone counts by team member role, stacked by milestone type. Date range: {startDate} to {endDate}</p>
        </div>
        <ExportButtons
          onCsv={() => exportRowsToCsv(flatExportRows, `team-member-performance-${startDate}-to-${endDate}.csv`)}
          onXlsx={() => exportSheetsToXlsx(workbookSheets, `team-member-performance-${startDate}-to-${endDate}.xlsx`)}
        />
      </div>
      <ErrorMessage message={error} />
      {loading ? <LoadingState message="Loading role summaries..." /> : (
        <>
          <div className="panel workbook-table-panel contribution-panel">
            <div className="section-bar">Team Members Contribution Summary</div>
            <DataTable
              columns={[
                { key: 'member_name', label: 'Team Member' },
                ...ROLE_DEFINITIONS.map((role) => ({ key: role.key, label: role.label, align: 'right', render: (value) => value || 0 })),
                { key: 'total_contribution', label: 'Total Contribution', align: 'right' },
              ]}
              rows={contributionSummary}
            />
          </div>

          {grouped.map((role, index) => (
            <section className="role-section" key={role.key}>
              <div className="section-heading section-bar soft-bar">
                <h3>{index + 1}. Milestones per {role.label}</h3>
                <span>{role.rows.length} team member{role.rows.length === 1 ? '' : 's'}</span>
              </div>
              <div className="grid-two">
                <div className="panel workbook-table-panel">
                  <DataTable columns={milestoneColumns} rows={role.rows} />
                </div>
                <ChartCard
                  title={`Milestones per ${role.label}, Stacked by Milestone Type`}
                  data={role.rows}
                  xKey="member_name"
                  type="stackedBar"
                  series={milestoneSeries}
                  height={Math.max(320, Math.min(560, role.rows.length * 28))}
                />
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
